import { Skeleton } from "antd-mobile";
import { debounce, pxChangeVw } from "@/utils";
import { getMainDomain } from "@/utils/getMainDomain";
import { useSunMoonAdCpt as useAdCpt } from "@/hooks/adCpt/pageAds";
import useDateFormat from "@/hooks/useDateFormat";
import useTranslate from "@/hooks/useTranslate";

// Moon phase images
import Svg02 from "@assets/icons/svg02.svg?react";
import Svg04 from "@assets/icons/svg04.svg?react";
import NewMoon from "@assets/img/NewMoon.png";
import WaxingCrescent from "@assets/img/WaxingCrescent.png";
import FirstQuarter from "@assets/img/FirstQuarter.png";
import WaxingGibbous from "@assets/img/WaxingGibbous.png";
import Full from "@assets/img/Full.png";
import WaningGibbous from "@assets/img/WaningGibbous.png";
import Last from "@assets/img/Last.png";
import WaningCrescent from "@assets/img/WaningCrescent.png";
import { is_en_gl_link, is_es_mx_link } from "@/i18n/lang";
import classNames from "classnames";
import { useCptUtil } from "./useCptUtil";
import { homeAtom } from "@/atoms/home";
import { querySunmoonAtom, sunmoonAtom } from "@/atoms/sunmoon";
import { isLongText } from "@/utils/isLongText";
import { getLangMap } from "@/utils/getLangMap.ts";
import TestTips1 from "@/components/TestTips1";

const MOON_PHASE_IMAGES: Record<any, string> = {
  NewMoon,
  WaxingCrescent,
  FirstQuarter,
  WaxingGibbous,
  Full,
  WaningGibbous,
  Last,
  WaningCrescent,
};

const SunMoonDefault: React.FC = () => {
  const { dayjs, userTimeZone } = useDayjs();
  const { $t } = useTranslate();
  const data = useAtomValue(sunmoonAtom);
  const { pathname } = useLocation();
  const { hostName } = useHostName();
  const { enDomains, shortDate, supportedDomains } = useDateFormat();
  const fetchSunMoonData = useSetAtom(homeAtom);
  const setSunMoonData = useSetAtom(querySunmoonAtom);
  useEffect(() => {
    fetchSunMoonData(pathname);
  }, []);
  const dayDiff = dayjs(data?.now_date).diff(dayjs().format("YYYY-MM-DD"), "day");
  const lan = getLangMap();
  const getFormattedDate1 = (timestamp: number) => {
    if (is_en_gl_link() || is_es_mx_link()) {
      return dayjs(timestamp).format("DD/MM/YYYY");
    }
    if (isLongText()) {
      return dayjs(timestamp).tz(userTimeZone).locale("ru").format("DD MMMM YYYY");
    }
    return dayjs(timestamp).format(
      enDomains.includes(hostName)
        ? "MMM DD, YYYY"
        : supportedDomains.includes(hostName)
          ? "DD/MM/YYYY"
          : ["zh-hant", "zh-hans"].includes(lan)
            ? "YYYY/MM/DD"
            : "YYYY-MM-DD",
    );
  };

  const getFormattedDate2 = (date: string) => {
    if (is_en_gl_link() || is_es_mx_link()) {
      return dayjs(date).format("DD/MM");
    }
    if (isLongText()) {
      return dayjs(date).tz(userTimeZone).locale("ru").format("DD MMMM");
    }
    return dayjs(date).format(enDomains.includes(hostName) ? "MMM DD" : shortDate);
  };
  const { getCpt01, getCpt02 } = useCptUtil({ data });

  const MoonPhaseContent = ({ contentRef }: any) => {
    return (
      <WeatherContent
        className={classNames(["rounded-10 backdrop-blur-10 bg-[var(--app-bg-08)] text-12 p-16"])}
        ref={contentRef}
      >
        <div className={classNames(["mb-27 color-[var(--app-text-01)]"])}>{$t("title06")}</div>
        <div className="mb-32">
          <div className="mb-24">{getCpt01()}</div>
        </div>
        <div className="flex flex-col gap-y-16">
          <div className="flex gap-x-24">
            <div className="flex-[1] flex items-center">
              <i
                className={classNames([
                  "flex-[1] inline-block color-[var(--app-text-16)] flex justify-start font-size-inherit",
                ])}
              >
                {$t("title12")}
              </i>
              <i className={classNames(["flex-[1] inline-block text-16 items-center"])}>{data?.SunMoon?.Sun?.Rise}</i>
            </div>
            <div className="flex-[1] flex items-center">
              <i className={classNames(["flex-[1] inline-block font-size-inherit"])}>{$t("title13")}</i>
              <i className={classNames(["flex-[1] inline-block text-16"])}>{data?.SunMoon?.Sun?.Set}</i>
            </div>
          </div>
          <div className="flex gap-x-24">
            <div className="flex-[1] flex items-center">
              <i
                className={classNames([
                  "flex-[1] inline-block color-[var(--app-text-16)] flex justify-start font-size-inherit",
                ])}
              >
                {$t("title14")}
              </i>
              <i className={classNames(["flex-[1] inline-block text-16 items-center"])}>{data?.SunMoon?.Moon?.Rise}</i>
            </div>
            <div className="flex-[1] flex items-center">
              <i className={classNames(["flex-[1] inline-block font-size-inherit"])}>{$t("title15")}</i>
              <i className={classNames(["flex-[1] flex justify-start items-end gap-x-4"])}>
                <i className="font-size-16">{data?.SunMoon?.Moon?.Set}</i>
                {!!data?.SunMoon?.Moon?.NextDay && data?.SunMoon?.Moon.NextDay > 0 && (
                  <sub className="text-10 translate-y-[-1.2px]">+{data?.SunMoon?.Moon.NextDay}</sub>
                )}
              </i>
            </div>
          </div>
        </div>
      </WeatherContent>
    );
  };

  const SunMoonScheduleContent = () => {
    return (
      <WeatherContent className={classNames(["rounded-10 backdrop-blur-10 bg-[var(--app-bg-08)] text-12 p-16"])}>
        <div className={classNames(["mb-27 color-[var(--app-text-01)]"])}>{$t("title07")}</div>
        <div
          className={classNames([
            "flex justify-around items-center border-b-1 border-b-solid border-b-[var(--app-border-05)] pb-25",
          ])}
          dir="auto"
        >
          <div>
            <Svg02
              pointerEvents={dayDiff <= 0 ? "none" : "auto"}
              className="active:bg-[var(--app-bg-21)]"
              style={{
                width: pxChangeVw("40px"),
                opacity: dayDiff <= 0 ? 0.5 : 1,
              }}
              onClick={debounce(() =>
                setSunMoonData({
                  test: isDevEnv ? 1 : undefined,
                  keyId: undefined,
                  now_date: data?.now_date,
                  type: "pre",
                }),
              )}
            />
          </div>
          <div className={classNames(["flex flex-col items-center gap-y-8 text-12"])}>
            <WeatherImage
              className="w-60"
              prefix={false}
              src={MOON_PHASE_IMAGES[data?.SunMoon?.Moon?.PhaseType] ?? NewMoon}
            />
            <p>{data?.SunMoon?.Moon?.Phase}</p>
            <p style={{ opacity: 0.7 }}>{getFormattedDate1(data?.now_date || Date.now())}</p>
          </div>
          <div>
            <Svg04
              pointerEvents={dayDiff >= 9 ? "none" : "auto"}
              className="active:bg-[var(--app-bg-21)]"
              style={{
                width: pxChangeVw("40px"),
                opacity: dayDiff >= 9 ? 0.5 : 1,
              }}
              onClick={debounce(() =>
                setSunMoonData({
                  test: isDevEnv ? 1 : undefined,
                  keyId: undefined,
                  now_date: data?.now_date,
                  type: "next",
                }),
              )}
            />
          </div>
        </div>
        <div className={classNames(["pt-20 flex text-12 color-[var(--app-text-14)]"])}>
          <div className="flex-[1] text-center border-r-1 border-r-solid border-r-[var(--app-border-05)]">
            <WeatherImage className={classNames(["w-20 mb-10"])} src={Full} prefix={false} />
            <div>
              <p>{$t("title08")}</p>
              <p className={classNames(["font-size-inherit"])}>{getFormattedDate2(data?.next_full_moon)}</p>
            </div>
          </div>
          <div className="flex-[1] text-center">
            <WeatherImage className={classNames(["w-20 mb-10"])} src={NewMoon} prefix={false} />
            <div>
              <p>{$t("title09")}</p>
              <p className={classNames(["font-size-inherit"])}>{getFormattedDate2(data?.next_new_moon)}</p>
            </div>
          </div>
        </div>
      </WeatherContent>
    );
  };
  const SunMoonSection = ({ contentRef }: any) => {
    return isEmptyType(data) ? (
      <Skeleton
        animated
        style={{
          "--width": "100%",
          "--height": pxChangeVw("317px"),
          "--border-radius": "10px",
        }}
      />
    ) : (
      <MoonPhaseContent contentRef={contentRef} />
    );
  };
  const MoonPhaseSection = () => {
    return isEmptyType(data) ? (
      <Skeleton
        animated
        style={{
          "--width": "100%",
          "--height": pxChangeVw("342px"),
          "--border-radius": "10px",
        }}
      />
    ) : (
      <SunMoonScheduleContent />
    );
  };

  return (
    <>
      <TestTips1 />
      {getCpt02({
        useAdCpt,
        pathname,
        MoonPhaseSection,
        SunMoonSection,
        getMainDomain,
      })}
    </>
  );
};

export default SunMoonDefault;
